import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'; // Sequence arrow
import BoltIcon from '@mui/icons-material/Bolt'; // Hotspot
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'; // Trigger point
import FlareIcon from '@mui/icons-material/Flare'; // Intensity Low
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'; // Intensity Moderate
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'; // Neutral
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied'; // Negative
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied'; // Positive
import TimelineIcon from '@mui/icons-material/Timeline'; // Arc
import WhatshotIcon from '@mui/icons-material/Whatshot'; // Intensity High
import {
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

// Helper for Emotion Intensity
const getIntensityStyle = (intensity) => {
  switch (intensity?.toLowerCase()) {
    case 'high':
      return { icon: <WhatshotIcon fontSize="small" />, color: 'error', label: 'High' };
    case 'moderate':
      return {
        icon: <LocalFireDepartmentIcon fontSize="small" />,
        color: 'warning',
        label: 'Moderate',
      };
    case 'low':
      return { icon: <FlareIcon fontSize="small" />, color: 'info', label: 'Low' };
    default:
      return { icon: null, color: 'default', label: intensity || 'N/A' };
  }
};

const SentimentSpectrumAnalysisTranscript = ({ summaryData }) => {
  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Sentiment Spectrum analysis data not available.
        </Box>
      </Card>
    );
  }

  const {
    summary,
    sentiment_distribution,
    emotion_hotspots = [],
    emotional_arcs = [],
  } = summaryData.results;

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Sentiment Spectrum Analysis
          <AnalysisLensHelperText lensName="sentiment_spectrum" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        {summary && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Analysis Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {summary}
            </Typography>
          </Box>
        )}

        {/* Sentiment Distribution */}
        {sentiment_distribution && (
          <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5 }}>
              Overall Sentiment Distribution (by Turn)
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-around"
            >
              <Chip
                icon={<SentimentVerySatisfiedIcon />}
                label={`Positive: ${sentiment_distribution.positive}`}
                color="success"
                variant="outlined"
                sx={{ flexGrow: 1 }}
              />
              <Chip
                icon={<SentimentNeutralIcon />}
                label={`Neutral: ${sentiment_distribution.neutral}`}
                color="default"
                variant="outlined"
                sx={{ flexGrow: 1 }}
              />
              <Chip
                icon={<SentimentVeryDissatisfiedIcon />}
                label={`Negative: ${sentiment_distribution.negative}`}
                color="error"
                variant="outlined"
                sx={{ flexGrow: 1 }}
              />
            </Stack>
          </Box>
        )}

        {/* Emotion Hotspots */}
        {emotion_hotspots.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <BoltIcon color="warning" /> Key Emotion Hotspots
            </Typography>
            <Stack spacing={2.5}>
              {emotion_hotspots.map((hotspot, index) => {
                const intensityStyle = getIntensityStyle(hotspot.intensity);
                return (
                  <Box
                    key={`hotspot-${index}`}
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack spacing={1}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                          Emotion: {hotspot.emotion}
                        </Typography>
                        <Chip
                          icon={intensityStyle.icon}
                          label={`Intensity: ${intensityStyle.label}`}
                          color={intensityStyle.color}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {hotspot.moment_description}
                      </Typography>
                      {hotspot.quotes?.length > 0 && (
                        <Box sx={{ pt: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Supporting Quotes:
                          </Typography>
                          <List dense disablePadding>
                            {hotspot.quotes.map((quote, idx) => (
                              <ListItem
                                key={idx}
                                disableGutters
                                sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 0.5 } }}
                              >
                                <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                                  <FormatQuoteIcon
                                    fontSize="small"
                                    sx={{ transform: 'scaleX(-1)' }}
                                    color="disabled"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={quote}
                                  primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Emotional Arcs */}
        {emotional_arcs.length > 0 && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <TimelineIcon color="primary" /> Identified Emotional Arcs
            </Typography>
            <Stack spacing={3}>
              {emotional_arcs.map((arc, index) => (
                <Box
                  key={`arc-${index}`}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      {arc.label}
                    </Typography>

                    {/* Sequence */}
                    {arc.sequence?.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Sequence:
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                          {arc.sequence.map((state, idx) => (
                            <React.Fragment key={`seq-${index}-${idx}`}>
                              <Chip label={state} size="small" variant="outlined" />
                              {idx < arc.sequence.length - 1 && (
                                <ArrowForwardIcon fontSize="small" color="disabled" />
                              )}
                            </React.Fragment>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Trigger Points */}
                    {arc.trigger_points?.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <FlagOutlinedIcon fontSize="inherit" /> Trigger Points:
                        </Typography>
                        <List dense disablePadding sx={{ pl: 1 }}>
                          {arc.trigger_points.map((trigger, idx) => (
                            <ListItem key={`trigger-${index}-${idx}`} disablePadding>
                              <ListItemText
                                primary={`• ${trigger}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Quotes */}
                    {arc.quotes?.length > 0 && (
                      <Box sx={{ pt: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Supporting Quotes:
                        </Typography>
                        <List dense disablePadding>
                          {arc.quotes.map((quote, idx) => (
                            <ListItem
                              key={`arc-quote-${index}-${idx}`}
                              disableGutters
                              sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 0.5 } }}
                            >
                              <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                                <FormatQuoteIcon
                                  fontSize="small"
                                  sx={{ transform: 'scaleX(-1)' }}
                                  color="disabled"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={quote}
                                primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default SentimentSpectrumAnalysisTranscript;
